import {
  CaseAppealListApiResponse,
  CaseAppealListPaginatedResult,
  FetchCaseAppealListParams,
  mapCaseAppealListRow,
} from "../../data-model/caseAppealsTable";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { getBearerToken } from "./bearerToken";

export type {
  CaseAppealListApiResponse,
  CaseAppealListDetails,
  CaseAppealListPaginatedResult,
  CaseAppealListPagination,
  CaseAppealListRow,
  FetchCaseAppealListParams,
} from "../../data-model/caseAppealsTable";

/**
 * Normalizes the appeal list API response for Case Appeals table hooks.
 *
 * @param response - Raw appeal list API response
 * @returns Paginated result with typed appeal rows
 */
export const normalizeAppealListResponse = (
  response: CaseAppealListApiResponse,
): CaseAppealListPaginatedResult => {
  const details = response.details;
  const rows = (details?.rows ?? []).map(mapCaseAppealListRow);
  const totalCount = details?.total ?? 0;
  const page = details?.page ?? 1;
  const pageSize = details?.limit ?? rows.length;

  return {
    success: response.success ?? Boolean(details?.rows),
    message: response.message ?? "",
    rows,
    row_count: rows.length,
    page,
    page_size: pageSize,
    total_count: totalCount,
    total_pages: pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1,
  };
};

/**
 * Fetches paginated appeal cases for the Case Appeals table.
 *
 * @param params - Page, limit, filters, and optional org override
 * @returns Normalized paginated result with typed appeal rows
 * @throws {Error} When auth is missing or the API returns a non-OK status
 */
export const fetchAppealList = async (
  params: FetchCaseAppealListParams,
): Promise<CaseAppealListPaginatedResult> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const body: Record<string, unknown> = {
      page: params.page ?? 1,
      limit: params.limit ?? 50,
      org_id: params.org_id ?? getOrgIdForFetchExternalWorklist(),
    };

    if (params.filters && Object.keys(params.filters).length > 0) {
      body.filters = params.filters;
    }

    // Only sent when searching; the API defaults to case-sensitive matching.
    if (params.case_sensitive !== undefined) {
      body.case_sensitive = params.case_sensitive;
    }

    const response = await fetch(API_ENDPOINTS.APPEAL_LIST, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    const responseText = await response.text();
    let parsed: CaseAppealListApiResponse = {};

    if (responseText.trim()) {
      try {
        parsed = JSON.parse(responseText) as CaseAppealListApiResponse;
      } catch {
        throw new Error(responseText || "Failed to parse appeal list response");
      }
    }

    if (!response.ok) {
      throw new Error(
        parsed.message ||
          `Failed to fetch appeal list: ${response.status} - ${responseText}`,
      );
    }

    const normalized = normalizeAppealListResponse(parsed);
    logDataToConsole("fetchAppealList", normalized);
    return normalized;
  } catch (error) {
    logError(error as Error, "fetchAppealList");
    throw error;
  }
};
